/**
 * Phone input with country prefix + flag picker.
 * - Renders SVG flag of the selected country
 * - Stores the value as E.164 (+355 6912345678)
 * - Uses libphonenumber-js for normalisation/validation
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Search, ChevronDown } from "lucide-react";
import {
    parsePhoneNumberFromString,
    getCountries,
    getCountryCallingCode,
    AsYouType,
} from "libphonenumber-js";
import Flag from "@/components/Flag";

// Minimal country names mapping for the most common countries.
// libphonenumber-js doesn't ship localized names — we only need to display
// the country in the picker so a simple Intl.DisplayNames lookup is enough.
function getCountryName(cc) {
    try {
        const dn = new Intl.DisplayNames(["en"], { type: "region" });
        return dn.of(cc) || cc;
    } catch { return cc; }
}

function countryList() {
    return getCountries().map((cc) => ({
        cc,
        name: getCountryName(cc),
        code: `+${getCountryCallingCode(cc)}`,
    })).sort((a, b) => a.name.localeCompare(b.name));
}

export default function PhoneInput({
    value,
    onChange,
    defaultCountry = "AL",
    label,
    required,
    testid = "phone-input",
    placeholder,
}) {
    const parsed = useMemo(() => value ? parsePhoneNumberFromString(value) : null, [value]);
    const initial = parsed?.country || defaultCountry;
    const [country, setCountry] = useState(initial);
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const ref = useRef(null);

    useEffect(() => {
        function onDocClick(e) {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        }
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    // Keep country in sync if value changes from parent (e.g. loaded record).
    useEffect(() => {
        if (parsed?.country && parsed.country !== country) setCountry(parsed.country);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parsed?.country]);

    const all = useMemo(() => countryList(), []);
    const filtered = useMemo(() => {
        if (!q) return all;
        const needle = q.toLowerCase();
        return all.filter((c) =>
            c.name.toLowerCase().includes(needle) ||
            c.cc.toLowerCase().includes(needle) ||
            c.code.includes(needle),
        );
    }, [q, all]);

    const dialCode = `+${getCountryCallingCode(country)}`;

    const handleNumberChange = (raw) => {
        // Strip the dial code (if user typed it), then re-attach in E.164.
        const cleaned = raw.replace(/[^\d+]/g, "");
        const national = cleaned.startsWith("+")
            ? cleaned.slice(dialCode.length).replace(/[^\d]/g, "")
            : cleaned;
        const formatted = new AsYouType(country).input(national);
        const e164 = `${dialCode}${national}`;
        onChange?.(national ? e164 : "", { country, formatted, national });
    };

    const nationalPart = useMemo(() => {
        if (!value) return "";
        const p = parsePhoneNumberFromString(value);
        if (p) return p.nationalNumber;
        return value.startsWith(dialCode) ? value.slice(dialCode.length) : value;
    }, [value, dialCode]);

    const displayValue = useMemo(() => {
        if (!nationalPart) return "";
        return new AsYouType(country).input(nationalPart);
    }, [nationalPart, country]);

    return (
        <label className="block" ref={ref}>
            {label && (
                <div className="text-[11px] font-medium text-muted-foreground mb-1">
                    {label}{required && <span className="text-red-600"> *</span>}
                </div>
            )}
            <div className="flex">
                {/* Country selector */}
                <button
                    type="button"
                    data-testid={`${testid}-country`}
                    onClick={() => setOpen((s) => !s)}
                    className="flex items-center gap-1.5 rounded-l-md border border-r-0 border-border bg-card px-2 py-2 text-[13px] hover:bg-muted/40"
                >
                    <Flag country={country} />
                    <span className="text-muted-foreground">{dialCode}</span>
                    <ChevronDown size={12} className="text-muted-foreground" />
                </button>
                {/* Number */}
                <input
                    type="tel"
                    data-testid={`${testid}-number`}
                    value={displayValue}
                    onChange={(e) => handleNumberChange(e.target.value)}
                    placeholder={placeholder || "69 123 4567"}
                    className="akm-input flex-1 rounded-l-none text-[13px]"
                />
            </div>
            {open && (
                <div
                    data-testid={`${testid}-menu`}
                    className="absolute z-50 mt-1 w-[300px] rounded-md border border-border bg-card shadow-lg overflow-hidden"
                >
                    <div className="border-b border-border p-2">
                        <div className="relative">
                            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <input
                                autoFocus
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search country…"
                                className="akm-input h-7 w-full pl-7 text-[12px]"
                                data-testid={`${testid}-search`}
                            />
                        </div>
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                        {filtered.map((c) => (
                            <button
                                key={c.cc}
                                type="button"
                                data-testid={`${testid}-opt-${c.cc}`}
                                onClick={() => {
                                    setCountry(c.cc);
                                    setOpen(false);
                                    setQ("");
                                    // Re-emit with new country prefix
                                    if (nationalPart) {
                                        const newE164 = `+${getCountryCallingCode(c.cc)}${nationalPart}`;
                                        onChange?.(newE164, { country: c.cc, national: nationalPart });
                                    }
                                }}
                                className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12.5px] hover:bg-muted/60 ${
                                    c.cc === country ? "bg-muted/40" : ""
                                }`}
                            >
                                <Flag country={c.cc} />
                                <span className="flex-1 truncate">{c.name}</span>
                                <span className="text-muted-foreground tabular-nums">{c.code}</span>
                            </button>
                        ))}
                        {filtered.length === 0 && (
                            <div className="px-3 py-4 text-center text-[12px] text-muted-foreground">No matches</div>
                        )}
                    </div>
                </div>
            )}
        </label>
    );
}
